import { Box, Center, Spinner, Text } from "@chakra-ui/react"
import { GoogleMap, InfoWindowF } from "@react-google-maps/api"
import Card from "components/card/Card"
import { AuthContext } from "contexts/AuthContext"
import { useDevice } from "hooks/useDevice"
import { useLocation } from "hooks/useLocation"
import { useMapView } from "hooks/useMapView"
import { useMapsReady } from "hooks/useMapsReady"
import { useSmoothWheelZoom } from "hooks/useSmoothWheelZoom"
import { useUserPosition } from "hooks/useUserPosition"
import { groupByPosition, initialView, ZOOM_ON_USER } from "lib/map"
import { isOffline } from "lib/presence"
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import LocationInfo from "./components/info"
import { Pins, UserDot } from "./components/layers"
import MapToolbar from "./components/toolbar"

export default function MapPage() {
  const { user } = useContext(AuthContext)
  const [locations, loading, error, refresh] = useLocation({ user })
  const [devices] = useDevice({ user })
  const mapsState = useMapsReady()
  const { position, status, locate } = useUserPosition()
  const [offline, setOffline] = useState(false)

  // The size of the map decides the zoom of the starting view
  const containerRef = useRef(null)
  const [size, setSize] = useState(null)
  useLayoutEffect(() => {
    const element = containerRef.current
    if (element) setSize({ width: element.clientWidth, height: element.clientHeight })
  }, [])

  // The starting view comes from ALL locations, so the "Offline" filter never moves the map
  const view = initialView({ userPosition: position, groups: groupByPosition(locations), size })

  // The first view goes into the options, which are created once and never change afterwards,
  // so the map is not reset by a re-render. Later changes of the view are applied by useMapView.
  const optionsRef = useRef(null)
  if (size && !optionsRef.current) {
    optionsRef.current = {
      center: view.center,
      zoom: view.zoom,
      streetViewControl: false,
      mapTypeControl: false,
      clickableIcons: false,
      // The wheel is handled by useSmoothWheelZoom: one level at a time
      scrollwheel: false,
      gestureHandling: "greedy",
    }
  }
  const options = optionsRef.current

  const [map, setMap] = useState(null)
  const onLoad = useCallback((instance) => setMap(instance), [])
  const onUnmount = useCallback(() => setMap(null), [])
  const { onDragStart, onZoomChanged, showAt } = useMapView(map, view)
  useSmoothWheelZoom(map)

  // "My location" asked before the browser knew the position: go there as soon as it arrives
  const wantsUser = useRef(false)
  useEffect(() => {
    if (wantsUser.current && position && map) {
      wantsUser.current = false
      showAt(position, ZOOM_ON_USER)
    }
  }, [position, map, showAt])
  const locateUser = () => {
    wantsUser.current = true
    locate()
  }

  // What is drawn: with "Offline" checked only the locations with a red warning triangle
  const offlineLocations = locations.filter(location => isOffline(location))
  const shown = offline ? offlineLocations : locations
  const groups = groupByPosition(shown)
  const onMap = groups.reduce((sum, group) => sum + group.ids.length, 0)

  const devicesByLocation = devices.reduce((byLocation, device) => {
    if (device.locationId) {
      (byLocation[device.locationId] = byLocation[device.locationId] || []).push(device)
    }
    return byLocation
  }, {})

  const [selectedKey, setSelectedKey] = useState(null)
  const selected = groups.find(group => group.key === selectedKey)
  const selectedLocations = selected
    ? selected.ids.map(id => shown.find(location => location.id === id)).filter(Boolean)
    : []

  const fallback = view.source === "locations" ? "your locations" : "Serbia"

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} flex="1" minH={0} display="flex" flexDirection="column">
      <Card direction="column" w="100%" px="0px" py="0px" overflow="hidden" flex="1" minH={0}>
        <MapToolbar
          onMap={onMap}
          withoutCoordinates={shown.length - onMap}
          status={status}
          hasPosition={!!position}
          mapReady={!!map}
          loading={loading}
          offline={offline}
          offlineCount={offlineLocations.length}
          onOfflineChange={setOffline}
          onLocate={locateUser}
          onShowUser={() => showAt(position, ZOOM_ON_USER)}
          onRefresh={refresh}
        />
        {error && <Text px="25px" pb="2" color="red.500" fontSize="sm">{String(error)}</Text>}
        {(status === "denied" || status === "unavailable") && (
          <Text px="25px" pb="2" color="gray" fontSize="sm">
            {status === "denied"
              ? `Access to your location is blocked, so the map shows ${fallback}.`
              : `Your location could not be determined, so the map shows ${fallback}.`}
          </Text>
        )}
        <Box ref={containerRef} position="relative" w="100%" flex="1" minH="260px">
          {mapsState === "ready" && options && (
            <GoogleMap
              mapContainerStyle={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
              options={options}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onDragStart={onDragStart}
              onZoomChanged={onZoomChanged}
              onClick={() => setSelectedKey(null)}
            >
              <Pins groups={groups} onSelect={setSelectedKey} />
              <UserDot position={position} />
              {selected && selectedLocations.length > 0 && (
                <InfoWindowF key={selected.key} position={selected.position} onCloseClick={() => setSelectedKey(null)}>
                  <LocationInfo locations={selectedLocations} devicesByLocation={devicesByLocation} />
                </InfoWindowF>
              )}
            </GoogleMap>
          )}
          {mapsState === "loading" && <Center h="100%"><Spinner size="xl" /></Center>}
          {mapsState === "failed" && (
            <Center h="100%"><Text color="gray">Map not available. Check the Google Maps API key and the connection.</Text></Center>
          )}
        </Box>
      </Card>
    </Box>
  )
}
